'use client';

import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { menuAPI, settingAPI } from '@/lib/api';
import Navigation from './Navigation';
import Link from 'next/link';
import { getImageUrl } from '@/lib/utils';
import SearchBox from './SearchBox';

export default function Header() {
  const { data: menus, isLoading: menusLoading, error: menusError } = useQuery({
    queryKey: ['menus', 'header'],
    queryFn: async () => {
      try {
        return await menuAPI.getList('header');
      } catch (error) {
        console.error('[Header] Failed to fetch menus:', error);
        // 返回空数组，使用空菜单
        return { data: [] };
      }
    },
    staleTime: 0, // 禁用缓存，总是获取最新数据
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchInterval: 30000, // 每30秒自动刷新一次
    retry: 1,
    retryDelay: 1000,
    throwOnError: false, // 即使失败也继续渲染
  });

  const { data: settingsResponse, isLoading: settingsLoading, error: settingsError } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      try {
        return await settingAPI.getAll();
      } catch (error) {
        console.error('[Header] Failed to fetch settings:', error);
        // 返回空对象，使用默认值
        return {};
      }
    },
    staleTime: 0, // 禁用缓存，总是获取最新数据
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 1, // 只重试1次
    retryDelay: 1000,
    throwOnError: false, // 即使失败也继续渲染
  });

  // API返回结构: { data: [{ id: "...", items: [...] }] }
  // 经过响应拦截器后: 直接返回 { data: [...] }
  // 所以 menus 就是 { data: [...] }
  const headerMenu = menus?.data?.[0];
  
  // 从API获取设置
  // settingAPI.getAll() 现在直接返回 Settings 对象，而不是包装在 { data: ... } 中
  // 所以 settingsResponse 就是 Settings 对象本身
  const settingsData = settingsResponse || {};
  
  // 使用设置值，如果为空则使用默认值
  const headerEmail = settingsData.header_email || '';
  const headerPhone = settingsData.header_phone || '';
  const siteLogo = settingsData.site_logo || '';
  const siteLogoText = settingsData.site_logo_text || 'LOGO';

  // 调试信息（开发环境）- 简化依赖项，避免无限循环
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Header Settings Debug:', {
        settingsResponse,
        settingsData,
        headerEmail,
        headerPhone,
        siteLogo,
        logoUrl: siteLogo ? getImageUrl(siteLogo) : null,
        isLoading: settingsLoading,
        error: settingsError
      });
      console.log('Header Menu Debug:', {
        menus,
        headerMenu,
        isLoading: menusLoading,
        error: menusError
      });
    }
  }, [settingsResponse, menus, settingsLoading, menusLoading, siteLogo]); // 只依赖关键值

  // 构建logo图片URL - 始终使用工具函数构建完整URL，确保图片能正确加载
  const logoUrl = siteLogo ? getImageUrl(siteLogo) : null;

  // Sticky Header JavaScript 特效实现 - 按照参考网站的智能实现方式
  useEffect(() => {
    let scrollHandler: (() => void) | null = null;
    let resizeHandler: (() => void) | null = null;
    
    // 等待 DOM 完全加载后初始化
    const initStickyHeader = () => {
      // 查找关键元素
      const mainHeaderWrap = document.querySelector('#main-header-sticky') as HTMLElement ||
                             document.querySelector('.ast-main-header-wrap.main-header-bar-wrap') as HTMLElement;
      const primaryHeaderBar = document.querySelector('.ast-primary-header-bar.ast-primary-header.main-header-bar') as HTMLElement;
      const masthead = document.getElementById('masthead');
      const astDesktopHeader = document.getElementById('ast-desktop-header');
      
      if (!mainHeaderWrap || !primaryHeaderBar) {
        // 如果元素不存在，延迟重试
        setTimeout(initStickyHeader, 50);
        return;
      }

      // 记录导航栏的初始高度（用于防止页面跳动）
      const originalHeight = primaryHeaderBar.offsetHeight;
      
      // 将初始高度存储到 data-height 属性中
      if (astDesktopHeader) {
        astDesktopHeader.setAttribute('data-height', originalHeight.toString());
      }
      primaryHeaderBar.setAttribute('data-height', originalHeight.toString());

      console.log('Sticky header initialized, original height:', originalHeight);

      const handleScroll = () => {
        const scrollTop = window.pageYOffset || 
                         document.documentElement.scrollTop || 
                         (document.body && document.body.scrollTop) || 
                         window.scrollY || 
                         0;
        const scrollThreshold = 0; // 参考网站使用 0 作为阈值

        if (scrollTop > scrollThreshold) {
          // 滚动时：添加粘性类
          primaryHeaderBar.classList.add('ast-sticky-active');
          mainHeaderWrap.classList.add('ast-sticky-active');
          if (masthead) masthead.classList.add('ast-sticky-header');
          document.body.classList.add('ast-primary-sticky-header-active');
          
          // 同步设置父容器高度，防止页面跳动
          const storedHeight = primaryHeaderBar.getAttribute('data-height') || originalHeight.toString();
          if (astDesktopHeader) {
            astDesktopHeader.style.height = storedHeight + 'px';
          }
          // 确保导航栏本身也有固定高度
          primaryHeaderBar.style.height = storedHeight + 'px';
        } else {
          // 滚动回顶部：移除粘性类
          primaryHeaderBar.classList.remove('ast-sticky-active');
          mainHeaderWrap.classList.remove('ast-sticky-active');
          if (masthead) masthead.classList.remove('ast-sticky-header');
          document.body.classList.remove('ast-primary-sticky-header-active');
          
          // 恢复自动高度
          if (astDesktopHeader) {
            astDesktopHeader.style.height = '';
          }
          primaryHeaderBar.style.height = '';
        }
      };

      // 初始检查
      handleScroll();

      // 监听滚动事件，使用节流优化性能
      let ticking = false;
      scrollHandler = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      };

      // 窗口大小改变时重新计算高度
      resizeHandler = () => {
        if (!document.body.classList.contains('ast-primary-sticky-header-active')) {
          // 只有在非 sticky 状态下才更新高度
          const newHeight = primaryHeaderBar.offsetHeight;
          primaryHeaderBar.setAttribute('data-height', newHeight.toString());
          if (astDesktopHeader) {
            astDesktopHeader.setAttribute('data-height', newHeight.toString());
          }
        }
        handleScroll();
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });
      window.addEventListener('resize', resizeHandler, { passive: true });
    };

    // 立即执行
    initStickyHeader();
    
    // 也使用 setTimeout 确保 DOM 已加载
    const timeoutId = setTimeout(() => {
      initStickyHeader();
    }, 200);

    // 清理函数
    return () => {
      clearTimeout(timeoutId);
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
      // 清理添加的类
      document.body.classList.remove('ast-primary-sticky-header-active');
    };
  }, []);

  return (
    <header 
      id="masthead" 
      className="site-header header-main-layout-1 ast-primary-menu-enabled ast-logo-title-inline ast-hide-custom-menu-mobile ast-builder-menu-toggle-icon ast-mobile-header-inline"
      itemType="https://schema.org/WPHeader"
      itemScope
      itemID="#masthead"
    >
      {/* Desktop Header - 精确匹配参考网站结构 */}
      <style dangerouslySetInnerHTML={{
        __html: `
          #ast-desktop-header .ast-container {
            max-width: 1200px !important;
            margin-left: auto !important;
            margin-right: auto !important;
            padding-left: 20px !important;
            padding-right: 20px !important;
            box-sizing: border-box !important;
            width: 100% !important;
          }
          #ast-desktop-header {
            overflow: visible !important;
          }
          /* 初始状态：导航栏处于正常文档流 */
          .ast-primary-header-bar {
            position: relative;
            transition: background-color 0.3s ease, box-shadow 0.3s ease, height 0.3s ease !important;
          }
          
          /* 激活粘性状态时的样式 - 使用 fixed 定位 */
          .ast-primary-header-bar.ast-sticky-active {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            z-index: 1000 !important;
            background-color: rgba(255, 255, 255, 0.95) !important;
            backdrop-filter: blur(10px);
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
            animation: fadeInDown 0.3s ease-out !important;
          }
          
          /* 当粘性导航激活时，为header的父容器设置固定高度，防止页面跳动 */
          body.ast-primary-sticky-header-active #ast-desktop-header {
            height: var(--ast-header-height, auto) !important;
          }
          
          /* 淡入动画 */
          @keyframes fadeInDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .ast-main-header-wrap.main-header-bar-wrap {
            position: relative !important;
            width: 100% !important;
            margin: 0 !important;
          }
          
          .ast-main-header-wrap.main-header-bar-wrap.ast-sticky-active {
            position: relative !important;
          }
          #masthead {
            overflow: visible !important;
            position: relative !important;
          }
          #masthead.ast-sticky-header {
            position: relative !important;
          }
          #ast-desktop-header .ast-main-header-wrap.main-header-bar-wrap {
            position: sticky !important;
            top: 0 !important;
          }
        `
      }} />
      <div id="ast-desktop-header" data-toggle-type="off-canvas" data-height="">
        {/* Above Header - 顶部信息栏 */}
        <div className="ast-above-header-wrap">
          <div className="ast-above-header-bar ast-above-header site-header-focus-item" data-section="section-above-header-builder">
            <div className="site-above-header-wrap ast-builder-grid-row-container site-header-focus-item ast-container" data-section="section-above-header-builder">
              <div className="ast-builder-grid-row ast-builder-grid-row-has-sides ast-builder-grid-row-no-center">
                {/* 左侧Widget区域 */}
                <div className="site-header-above-section-left site-header-section ast-flex site-header-section-left">
                  <aside className="header-widget-area widget-area site-header-focus-item header-widget-area-inner" data-section="sidebar-widgets-header-widget-1" aria-label="Header Widget 1">
                    <section id="elementor-library-3" className="widget widget_elementor-library">
                      <div className="elementor elementor-47" data-elementor-type="section" data-elementor-id="47" data-elementor-post-type="elementor_library">
                        <section className="elementor-section elementor-top-section elementor-element elementor-element-6a54729 elementor-section-boxed elementor-section-height-default elementor-section-height-default" data-id="6a54729" data-element_type="section">
                          <div className="elementor-container elementor-column-gap-default">
                            <div className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-13a54b8" data-id="13a54b8" data-element_type="column">
                              <div className="elementor-widget-wrap elementor-element-populated">
                                {/* 电话图标和文本 */}
                                {headerPhone ? (
                                  <>
                                    <div className="elementor-element elementor-element-1c2a79f elementor-widget__width-auto elementor-view-default elementor-widget elementor-widget-icon" data-id="1c2a79f" data-element_type="widget" data-widget_type="icon.default">
                                      <div className="elementor-widget-container">
                                        <div className="elementor-icon-wrapper">
                                          <div className="elementor-icon">
                                            <i aria-hidden="true" className="fas fa-phone-alt"></i>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="elementor-element elementor-element-4b0034f elementor-widget__width-auto elementor-widget elementor-widget-heading" data-id="4b0034f" data-element_type="widget" data-widget_type="heading.default">
                                      <div className="elementor-widget-container">
                                        <div className="elementor-heading-title elementor-size-default">Tel: {headerPhone}</div>
                                      </div>
                                    </div>
                                  </>
                                ) : null}
                                {/* 邮箱图标和文本 */}
                                {headerEmail ? (
                                  <>
                                    <div className="elementor-element elementor-element-6307539 elementor-widget__width-auto elementor-view-default elementor-widget elementor-widget-icon" data-id="6307539" data-element_type="widget" data-widget_type="icon.default">
                                      <div className="elementor-widget-container">
                                        <div className="elementor-icon-wrapper">
                                          <div className="elementor-icon">
                                            <i aria-hidden="true" className="fas fa-envelope"></i>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="elementor-element elementor-element-2a3624d elementor-widget__width-auto elementor-widget elementor-widget-heading" data-id="2a3624d" data-element_type="widget" data-widget_type="heading.default">
                                      <div className="elementor-widget-container">
                                        <div className="elementor-heading-title elementor-size-default">Email: {headerEmail}</div>
                                      </div>
                                    </div>
                                  </>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </section>
                      </div>
                    </section>
                  </aside>
                </div>
                
                {/* 右侧搜索框 */}
                <div className="site-header-above-section-right site-header-section ast-flex ast-grid-right-section">
                  <SearchBox />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Header - 主头部栏 */}
        <div 
          className="ast-main-header-wrap main-header-bar-wrap" 
          id="main-header-sticky"
          style={{ 
            position: 'sticky', 
            top: 0, 
            zIndex: 9999, 
            backgroundColor: '#ffffff', 
            width: '100%', 
            margin: 0,
            left: 0,
            right: 0
          }}
        >
          <div className="ast-primary-header-bar ast-primary-header main-header-bar site-header-focus-item" data-section="section-primary-header-builder">
            <div className="site-primary-header-wrap ast-builder-grid-row-container site-header-focus-item ast-container" data-section="section-primary-header-builder">
              <div className="ast-builder-grid-row ast-builder-grid-row-has-sides ast-builder-grid-row-no-center">
                {/* Logo区域 - 左侧 */}
                <div className="site-header-primary-section-left site-header-section ast-flex site-header-section-left">
                  <div className="ast-builder-layout-element ast-flex site-header-focus-item" data-section="title_tagline">
                    <div className="site-branding ast-site-identity" itemType="https://schema.org/Organization" itemScope>
                      <span className="site-logo-img">
                        <Link href="/" className="custom-logo-link" rel="home" aria-current="page">
                          {logoUrl ? (
                            <img
                              src={logoUrl}
                              alt={siteLogoText}
                              className="custom-logo"
                              style={{ width: 'auto', height: 'auto', maxWidth: '77px', maxHeight: '41px', display: 'block' }}
                              onError={(e) => {
                                console.error('Logo image failed to load:', logoUrl);
                                // 如果图片加载失败，隐藏图片元素，显示文本
                                const target = e.target as HTMLImageElement;
                                if (target.parentElement) {
                                  target.style.display = 'none';
                                  const textDiv = document.createElement('div');
                                  textDiv.className = 'text-2xl font-bold text-primary';
                                  textDiv.textContent = siteLogoText;
                                  target.parentElement.appendChild(textDiv);
                                }
                              }}
                            />
                          ) : (
                            <div className="text-2xl font-bold text-primary">{siteLogoText}</div>
                          )}
                        </Link>
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* 导航菜单区域 - 右侧 */}
                <div className="site-header-primary-section-right site-header-section ast-flex ast-grid-right-section">
                  <div className="ast-builder-menu-1 ast-builder-menu ast-flex ast-builder-menu-1-focus-item ast-builder-layout-element site-header-focus-item" data-section="section-hb-menu-1">
                    <div className="ast-main-header-bar-alignment">
                      <div className="main-header-bar-navigation">
                        <Navigation menu={headerMenu} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header - 移动端Header */}
      <div id="ast-mobile-header" className="ast-mobile-header-wrap " data-type="off-canvas">
        <div className="ast-main-header-wrap main-header-bar-wrap">
          <div className="ast-primary-header-bar ast-primary-header main-header-bar site-primary-header-wrap site-header-focus-item ast-builder-grid-row-layout-default ast-builder-grid-row-tablet-layout-default ast-builder-grid-row-mobile-layout-default" data-section="section-primary-header-builder">
            <div className="ast-builder-grid-row ast-builder-grid-row-has-sides ast-builder-grid-row-no-center">
              <div className="site-header-primary-section-left site-header-section ast-flex site-header-section-left">
                <div className="ast-builder-layout-element ast-flex site-header-focus-item" data-section="title_tagline">
                  <div className="site-branding ast-site-identity" itemType="https://schema.org/Organization" itemScope>
                    <span className="site-logo-img">
                      <Link href="/" className="custom-logo-link" rel="home" aria-current="page">
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt={siteLogoText}
                            className="custom-logo"
                            style={{ width: 'auto', height: 'auto', maxWidth: '77px', maxHeight: '41px', display: 'block' }}
                            onError={(e) => {
                              console.error('Logo image failed to load:', logoUrl);
                              // 如果图片加载失败，隐藏图片元素，显示文本
                              const target = e.target as HTMLImageElement;
                              if (target.parentElement) {
                                target.style.display = 'none';
                                const textDiv = document.createElement('div');
                                textDiv.className = 'text-2xl font-bold text-primary';
                                textDiv.textContent = siteLogoText;
                                target.parentElement.appendChild(textDiv);
                              }
                            }}
                          />
                        ) : (
                          <div className="text-2xl font-bold text-primary">{siteLogoText}</div>
                        )}
                      </Link>
                    </span>
                  </div>
                </div>
              </div>
              <div className="site-header-primary-section-right site-header-section ast-flex ast-grid-right-section">
                <div className="ast-builder-layout-element ast-flex site-header-focus-item" data-section="section-header-mobile-trigger">
                  <div className="ast-button-wrap">
                    <button type="button" className="menu-toggle main-header-menu-toggle ast-mobile-menu-trigger-minimal" aria-expanded="false" aria-label="Main menu toggle" style={{ display: 'flex' }}>
                      <span className="mobile-menu-toggle-icon">
                        <span aria-hidden="true" className="ahfb-svg-iconset ast-inline-flex svg-baseline">
                          <svg className="ast-mobile-svg ast-menu-svg" fill="currentColor" version="1.1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path d="M3 13h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1zM3 7h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1zM3 19h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1z"></path>
                          </svg>
                        </span>
                        <span aria-hidden="true" className="ahfb-svg-iconset ast-inline-flex svg-baseline">
                          <svg className="ast-mobile-svg ast-close-svg" fill="currentColor" version="1.1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path d="M5.293 6.707l5.293 5.293-5.293 5.293c-0.391 0.391-0.391 1.024 0 1.414s1.024 0.391 1.414 0l5.293-5.293 5.293 5.293c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414l-5.293-5.293 5.293-5.293c0.391-0.391 0.391-1.024 0-1.414s-1.024-0.391-1.414 0l-5.293 5.293-5.293-5.293c-0.391-0.391-1.024-0.391-1.414 0s-0.391 1.024 0 1.414z"></path>
                          </svg>
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Header - 固定Header（用于粘性头部） */}
      <div data-stick-gutter="0" data-stick-maxwidth="1056">
        <header 
          id="ast-fixed-header" 
          className="site-header header-main-layout-1 ast-primary-menu-enabled ast-logo-title-inline ast-hide-custom-menu-mobile ast-builder-menu-toggle-icon ast-mobile-header-inline ast-sticky-shrunk ast-header-fade" 
          style={{ visibility: 'hidden', opacity: 0, top: '0px' }}
          data-type="fixed-header" 
          data-stick-support="on" 
          data-stick-maxwidth="1348"
        >
          <div id="ast-desktop-header" data-toggle-type="off-canvas">
            <div className="ast-main-header-wrap main-header-bar-wrap ">
              <div className="ast-primary-header-bar ast-primary-header main-header-bar site-header-focus-item" data-section="section-primary-header-builder">
                <div className="site-primary-header-wrap ast-builder-grid-row-container site-header-focus-item ast-container" data-section="section-primary-header-builder">
                  <div className="ast-builder-grid-row ast-builder-grid-row-has-sides ast-builder-grid-row-no-center">
                    {/* Logo区域 - 左侧 */}
                    <div className="site-header-primary-section-left site-header-section ast-flex site-header-section-left">
                      <div className="ast-builder-layout-element ast-flex site-header-focus-item" data-section="title_tagline">
                        <div className="site-branding ast-site-identity" itemType="https://schema.org/Organization" itemScope>
                          <span className="site-logo-img">
                            <Link href="/" className="custom-logo-link" rel="home" aria-current="page">
                              {logoUrl ? (
                                <img
                                  src={logoUrl}
                                  alt={siteLogoText}
                                  className="custom-logo"
                                  style={{ width: 'auto', height: 'auto', maxWidth: '77px', maxHeight: '41px', display: 'block' }}
                                  onError={(e) => {
                                    console.error('Logo image failed to load:', logoUrl);
                                    const target = e.target as HTMLImageElement;
                                    if (target.parentElement) {
                                      target.style.display = 'none';
                                      const textDiv = document.createElement('div');
                                      textDiv.className = 'text-2xl font-bold text-primary';
                                      textDiv.textContent = siteLogoText;
                                      target.parentElement.appendChild(textDiv);
                                    }
                                  }}
                                />
                              ) : (
                                <div className="text-2xl font-bold text-primary">{siteLogoText}</div>
                              )}
                            </Link>
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* 导航菜单区域 - 右侧 */}
                    <div className="site-header-primary-section-right site-header-section ast-flex ast-grid-right-section">
                      <div className="ast-builder-menu-1 ast-builder-menu ast-flex ast-builder-menu-1-focus-item ast-builder-layout-element site-header-focus-item" data-section="section-hb-menu-1">
                        <div className="ast-main-header-bar-alignment">
                          <div className="main-header-bar-navigation">
                            <Navigation menu={headerMenu} sticky={true} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Mobile Header for Fixed Header */}
          <div id="ast-mobile-header" className="ast-mobile-header-wrap " data-type="off-canvas">
            <div className="ast-main-header-wrap main-header-bar-wrap">
              <div className="ast-primary-header-bar ast-primary-header main-header-bar site-primary-header-wrap site-header-focus-item ast-builder-grid-row-layout-default ast-builder-grid-row-tablet-layout-default ast-builder-grid-row-mobile-layout-default" data-section="section-primary-header-builder">
                <div className="ast-builder-grid-row ast-builder-grid-row-has-sides ast-builder-grid-row-no-center">
                  <div className="site-header-primary-section-left site-header-section ast-flex site-header-section-left">
                    <div className="ast-builder-layout-element ast-flex site-header-focus-item" data-section="title_tagline">
                      <div className="site-branding ast-site-identity" itemType="https://schema.org/Organization" itemScope>
                        <span className="site-logo-img">
                          <Link href="/" className="custom-logo-link" rel="home" aria-current="page">
                            {logoUrl ? (
                              <img
                                src={logoUrl}
                                alt={siteLogoText}
                                className="custom-logo"
                                style={{ width: 'auto', height: 'auto', maxWidth: '77px', maxHeight: '41px', display: 'block' }}
                                onError={(e) => {
                                  console.error('Logo image failed to load:', logoUrl);
                                  const target = e.target as HTMLImageElement;
                                  if (target.parentElement) {
                                    target.style.display = 'none';
                                    const textDiv = document.createElement('div');
                                    textDiv.className = 'text-2xl font-bold text-primary';
                                    textDiv.textContent = siteLogoText;
                                    target.parentElement.appendChild(textDiv);
                                  }
                                }}
                              />
                            ) : (
                              <div className="text-2xl font-bold text-primary">{siteLogoText}</div>
                            )}
                          </Link>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="site-header-primary-section-right site-header-section ast-flex ast-grid-right-section">
                    <div className="ast-builder-layout-element ast-flex site-header-focus-item" data-section="section-header-mobile-trigger">
                      <div className="ast-button-wrap">
                        <button type="button" className="menu-toggle main-header-menu-toggle ast-mobile-menu-trigger-minimal" aria-expanded="false" aria-label="Main menu toggle" style={{ display: 'flex' }}>
                          <span className="mobile-menu-toggle-icon">
                            <span aria-hidden="true" className="ahfb-svg-iconset ast-inline-flex svg-baseline">
                              <svg className="ast-mobile-svg ast-menu-svg" fill="currentColor" version="1.1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path d="M3 13h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1zM3 7h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1zM3 19h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1z"></path>
                              </svg>
                            </span>
                            <span aria-hidden="true" className="ahfb-svg-iconset ast-inline-flex svg-baseline">
                              <svg className="ast-mobile-svg ast-close-svg" fill="currentColor" version="1.1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path d="M5.293 6.707l5.293 5.293-5.293 5.293c-0.391 0.391-0.391 1.024 0 1.414s1.024 0.391 1.414 0l5.293-5.293 5.293 5.293c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414l-5.293-5.293 5.293-5.293c0.391-0.391 0.391-1.024 0-1.414s-1.024-0.391-1.414 0l-5.293 5.293-5.293-5.293c-0.391-0.391-1.024-0.391-1.414 0s-0.391 1.024 0 1.414z"></path>
                              </svg>
                            </span>
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>
      </div>
    </header>
  );
}

